import React, { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { adminApi, type StagingTestUser } from '../services/api';

export function StagingTestUserPicker({ value, onChange, disabled, readOnly = false }: {
  value: string[];
  onChange: (ids: string[]) => void;
  disabled: boolean;
  readOnly?: boolean;
}) {
  const { t } = useTranslation();
  const id = useId();
  const [term, setTerm] = useState('');
  const [users, setUsers] = useState<StagingTestUser[]>([]);
  const [knownUsers, setKnownUsers] = useState<Record<string, StagingTestUser>>({});
  const [state, setState] = useState<'loading' | 'done' | 'failed'>('loading');
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (disabled) return;
    let active = true;
    setState('loading');
    const timer = window.setTimeout(() => {
      adminApi.getStagingTestUsers(term.trim()).then(result => {
        if (!active) return;
        setUsers(result.users);
        setKnownUsers(previous => ({ ...previous, ...Object.fromEntries([...result.users, ...result.selectedUsers].map(user => [user.id, user])) }));
        setState('done');
      }).catch(() => { if (active) setState('failed'); });
    }, term ? 250 : 0);
    return () => { active = false; window.clearTimeout(timer); };
  }, [term, disabled, attempt]);

  const role = (user: StagingTestUser) => t(user.isAdmin ? 'persona.admin' : user.isSupport ? 'persona.support'
    : `localBankIdDebug.personas.${user.personaType === 'person' ? 'personSignedIn' : user.personaType}`);
  const details = (user: StagingTestUser) => <>
    <strong className="d-block text-break">{user.fullName}</strong>
    <span className="d-block small text-break">{user.email || t('localBankIdDebug.noEmail')}</span>
    <span className="d-block small text-muted">{role(user)}</span>
  </>;
  const change = (userId: string, selected: boolean) => onChange(selected ? [...value, userId] : value.filter(item => item !== userId));

  if (disabled) return <p className="text-muted mb-0">{t('adminDebugSettings.stagingTesters.unavailable')}</p>;
  return <fieldset disabled={readOnly}>
    <legend className="h5">{t('adminDebugSettings.stagingTesters.title')}</legend>
    <p id={`${id}-help`} className="text-explain">{t('adminDebugSettings.stagingTesters.help')}</p>
    <label className="form-label" htmlFor={`${id}-search`}>{t('adminDebugSettings.stagingTesters.search')}</label>
    <input id={`${id}-search`} type="search" className="form-control form-control-lg mb-3" value={term}
      maxLength={100} autoComplete="off" aria-describedby={`${id}-help`} onChange={event => setTerm(event.target.value)} />
    <div aria-live="polite">
      {state === 'loading' && <p role="status">{t('common.loading')}</p>}
      {state === 'failed' && <div role="alert">
        <p>{t('adminDebugSettings.stagingTesters.error')}</p>
        <button type="button" className="btn btn-outline-secondary btn-lg" onClick={() => setAttempt(count => count + 1)}>{t('localBankIdDebug.retry')}</button>
      </div>}
      {state === 'done' && !users.length && <p>{t('adminDebugSettings.stagingTesters.empty')}</p>}
      {state === 'done' && <div className="list-group mb-3">
        {users.map(user => <label key={user.id} className="list-group-item d-flex gap-3 align-items-start py-3">
          <input className="form-check-input mt-1 flex-shrink-0" type="checkbox" checked={value.includes(user.id)}
            onChange={event => change(user.id, event.target.checked)} />
          <span className="text-break">{details(user)}</span>
        </label>)}
      </div>}
      {state === 'done' && users.length === 100 && <p className="small text-muted">{t('adminDebugSettings.stagingTesters.refine')}</p>}
    </div>
    <h6>{t('adminDebugSettings.stagingTesters.selected', { count: value.length })}</h6>
    {!value.length && <p className="small text-muted mb-0">{t('adminDebugSettings.stagingTesters.none')}</p>}
    <ul className="list-unstyled mb-0">
      {value.map(userId => <li key={userId} className="d-flex flex-wrap align-items-center justify-content-between gap-2 border rounded p-3 mb-2">
        <span>{knownUsers[userId] ? details(knownUsers[userId]) : t('adminDebugSettings.stagingTesters.unavailableAccount')}</span>
        <button type="button" className="btn btn-outline-secondary btn-lg" onClick={() => change(userId, false)}>{t('adminDebugSettings.stagingTesters.remove')}</button>
      </li>)}
    </ul>
  </fieldset>;
}
